import { IconBell, IconEmail, IconException, IconInfoCircle, List, ListItem, MobileSheet, Pill } from "@geotab/zenith";
import React, { FC, useEffect, useRef, useState } from "react";
import { useTranslationContext } from "../../../../../ui/framework/hooks/TranslationContext";
import { getPageState, ItemListData } from "../helpers/Helper";
import Notifications, { INotificationWithId } from "../../../../../utils/notifications";
import User from "../../../../../model/User";
import { useUserServiceContext } from "../../../../../ui/framework/hooks/UserServiceContext";
import { dispatcher, DispatcherEvents } from "../../../../../utils/dispatcher";
import { useRouterContext } from "../../../../../ui/framework/hooks/RouterContext";
import { useStoreContext } from "../../../../../ui/framework/hooks/StoreContext";

const NotificationDialog: FC<{notificationMobileSheet: boolean; setNotificationMobileSheet: React.Dispatch<React.SetStateAction<boolean>>}> =
    ({ notificationMobileSheet, setNotificationMobileSheet }) => {

    const { translate } = useTranslationContext();
    const { userService } = useUserServiceContext();
    const { router } = useRouterContext();
    const { store } = useStoreContext();

    const ref = useRef(null);

    // const [navState, setNavState] = useState("");
    const [notifications, setNotifications] = useState({
        notification: [] as INotificationWithId[],
        eventsCount: 0,
        messagesCount: 0,
        hasUrgentNotifications: false
    });
    const [otherMessages, setOtherMessage] = useState<INotificationWithId[]>([]);

    const navigate = (path: string) => {
        if (getPageState(store).isLocked) {
            return;
        }

        setNotificationMobileSheet(false);
        router.navigate(path, true);
    };

    const list: ItemListData[] = [
        { id: "events", icon: <IconBell size="huge" />, primary: translate("events"), status: notifications.eventsCount, onClick: () => navigate("notifications/events")},
        { id: "messages", icon: <IconEmail size="huge" />, primary: translate("messages"), status: notifications.messagesCount, onClick: () => navigate("messaging/main")}
    ];

    const updateState = () => {
        const userId = new User(userService.getCurrentUser()).id;
        let userNotifications = Notifications.getVisible(userId);
        userNotifications.sort((x, y) => (x.critical === y.critical) ? 0 : (x.critical ? -1 : 1));

        let displayUnReadEvent = userNotifications.filter((notification) => notification.type !== "messaging" && notification.id !== "unreadEvents");
        let eventsCount = userNotifications.find((notification) => notification.type === "system" && notification.id === "unreadEvents")?.count || 0;
        let messagesCount = userNotifications.find((notification) => notification.type === "messaging" && notification.id === "unreadMessages")?.count || 0;
        let otherMessage = userNotifications.filter((notification) => notification.type === "messaging" && notification.id !== "unreadMessages");
        setOtherMessage(otherMessage);

        let evenCounts = eventsCount || 0;
        let messageCounts = messagesCount || 0;

        setNotifications({
            notification: displayUnReadEvent,
            eventsCount: evenCounts,
            messagesCount: messageCounts,
            hasUrgentNotifications: Notifications.hasCritical(userId)
        });
    };

    useEffect(() => {
        updateState();
        dispatcher.listenTo(dispatcher, DispatcherEvents.NotificationsChange, updateState);
    }, []);

    useEffect(() => {
        updateState();
    }, [notificationMobileSheet]);

    const displayStatus = (item: ItemListData) => {
        const showErrorPill = item.id === "events" && notifications.hasUrgentNotifications && notifications.eventsCount > 0;
        return (
            <Pill type={showErrorPill ? "error" : "info"}>
                <span className={item.id === "events" ? "top-bar-events-count" : "top-bar-notifications-count"} style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                    { item.status }
                </span>
            </Pill>
        );
    };

    const getIconBySeverity = (critical: boolean | undefined) => {
        if (critical) {
            return (
                <div className="zen-item-data__icon critical-notification"><IconException size="huge" /></div>
            );
        }

        return (
            <div className="zen-item-data__icon"><IconInfoCircle size="huge" /></div>
        );
    };

    const renderUnReadNotification = () => {
        return (
            <List>
                {notifications.notification.map((notification, index) => {
                    return (
                        <ListItem
                            key={index}
                            image={getIconBySeverity(notification.critical)}
                            id={notification.id}
                            name={notification.message}
                            onClick={notification.action}
                        />
                    );
                })}
            </List>
        );
    };

    const renderList = () => {
        return (
            <List>
                {Object.keys(list).map((key, index) => {
                    const item: ItemListData = list[key];
                    return (
                        <ListItem key={index} image={<div className="zen-item-data__icon">{item.icon as React.ReactNode}</div>} id={item.id} name={item.primary} onClick={item.onClick} status={displayStatus(item)}  />
                    );
                })}
            </List>
        );
    };

    const renderOtherMessages = () => {
        return (
            <List>
                {otherMessages.map((message, index) => {
                    return (
                        <ListItem
                            key={index}
                            image={getIconBySeverity(message.critical)}
                            id={message.id}
                            name={message.message}
                            onClick={message.action}
                        />
                    );
                })}
            </List>
        );
    };

    return (
        <>
            <MobileSheet
                label={translate("Notifications")}
                triggerRef={ref}
                isOpen={notificationMobileSheet}
                onHidePanel={() => setNotificationMobileSheet(false)}
                onCloseClick={() => setNotificationMobileSheet(false)}>
                <MobileSheet.Title>{translate("Notifications")}</MobileSheet.Title>
                <MobileSheet.Content>
                    { renderUnReadNotification() }
                    { renderList() }
                    { renderOtherMessages()}
                </MobileSheet.Content>
            </MobileSheet>
        </>
    );
};

export default NotificationDialog;